import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DadosCamposPorRegistro } from 'utils/types/blocoRegistros';
import { IModificadorCategoria } from 'utils/types/modificadorCategoria';

import AddEditRegistrosForm, {
  AddEditRegistrosFormHandle,
} from 'components/AddEditRegistrosForm/AddEditRegistrosForm';
import CustomModal from 'components/CustomModal';
import { useSaveRegistro } from 'components/DynamicDataTable/dynamicDataTable.service';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dadosCamposPorRegistro: DadosCamposPorRegistro;
  registroCodigo: string;
  processamentoSpedId: string;
  modo: 'add' | 'edit';
  registroSelecionadoIndex?: number | null;
  refetchCamposPorRegistro: () => void;
  paiLoaderId: number;
  modificadoresCategoriaBloco?: IModificadorCategoria[];
  modificadoresCategoriaRegistro?: IModificadorCategoria[];
  modificadorRegistroAbaAtual?: number | null;
}

const AddEditRegistrosModal: React.FC<Props> = ({
  isOpen,
  onClose,
  dadosCamposPorRegistro,
  registroCodigo,
  processamentoSpedId,
  modo,
  registroSelecionadoIndex = null,
  refetchCamposPorRegistro,
  paiLoaderId,
  modificadoresCategoriaBloco,
  modificadoresCategoriaRegistro,
  modificadorRegistroAbaAtual,
}) => {
  const queryClient = useQueryClient();
  const { campos, valores } = dadosCamposPorRegistro;
  const { mutate: salvarRegistro, isPending: isSaving } = useSaveRegistro(
    processamentoSpedId,
    registroCodigo,
    modo === 'edit',
    queryClient,
  );

  const formRef = useRef<AddEditRegistrosFormHandle>(null);

  const [dadosFormulario, setDadosFormulario] =
    useState<DadosCamposPorRegistro>({
      campos,
      valores: {
        pagina: 1,
        totalConteudo: 0,
        quantidadeNaPagina: 1,
        conteudo: [],
      },
    });

  const converterDadosSalvar = (values: any, campos: any[]) => {
    const valoresConvertidos = { ...values };

    campos.forEach((campo) => {
      if (campo.tipoCampo === 'number') {
        const valor = valoresConvertidos[campo.referencia];
        if (
          valor !== null &&
          valor !== '' &&
          typeof valor === 'string' &&
          valor.length <= 21
        ) {
          const valorConvertido = parseFloat(valor.replace(',', '.'));
          valoresConvertidos[campo.referencia] = isNaN(valorConvertido)
            ? null
            : valorConvertido;
        }
      } else if (campo.tipoCampo === 'date') {
        valoresConvertidos[campo.referencia] = !!valoresConvertidos[
          campo.referencia
        ]
          ? valoresConvertidos[campo.referencia]
          : null;
      }
    });

    return valoresConvertidos;
  };

  useEffect(() => {
    const registroVazio = campos.reduce((acc, campo) => {
      acc[campo.referencia] = '';
      return acc;
    }, {} as any);

    const registroEditado =
      modo === 'edit' && registroSelecionadoIndex !== null
        ? (valores.conteudo?.[registroSelecionadoIndex] ?? registroVazio)
        : registroVazio;

    setDadosFormulario({
      campos,
      valores: {
        pagina: 1,
        totalConteudo: 1,
        quantidadeNaPagina: 1,
        conteudo: [{ ...registroEditado }],
      },
    });
  }, [modo, campos, valores.conteudo, registroSelecionadoIndex]);

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${modo === 'add' ? 'Adicionar' : 'Editar'} Registro - ${registroCodigo}`}
      content={
        <AddEditRegistrosForm
          ref={formRef}
          dados={dadosFormulario}
          modo={modo}
          registroCodigo={registroCodigo}
          processamentoSpedId={processamentoSpedId}
          modificadorRegistroAbaAtual={modificadorRegistroAbaAtual}
          onSubmit={async (values) => {
            const valoresConvertidos = converterDadosSalvar(values, campos);
            valoresConvertidos['ID_PAI'] = paiLoaderId;

            salvarRegistro(valoresConvertidos, {
              onSuccess: () => {
                toast.success('Registro salvo com sucesso!');
                refetchCamposPorRegistro();
                onClose();
              },
              onError: () => {
                toast.error('Não foi possível salvar o registro.');
              },
            });
          }}
          modificadoresCategoriaBloco={modificadoresCategoriaBloco}
          modificadoresCategoriaRegistro={modificadoresCategoriaRegistro}
        />
      }
      cancelButtonText="Fechar"
      confirmButtonText={isSaving ? 'Salvando...' : 'Salvar'}
      confirmButtonDisabled={isSaving}
      confirmButtonAction={() => {
        formRef.current?.submit();
      }}
    />
  );
};

export default AddEditRegistrosModal;
